import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import Navbar from '../components/Navbar';
import { LotteryBallGroup } from '../components/LotteryBall';
import DisclaimerBanner from '../components/DisclaimerBanner';
import PredictionPaymentModal from '../components/PredictionPaymentModal';
import { toast } from 'sonner';
import { 
  Clock, 
  TrendingUp, 
  Calendar, 
  ArrowRight, 
  Flame, 
  Snowflake,
  BarChart3,
  Sparkles,
  RefreshCw,
  Brain
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const Dashboard = () => {
  const { token, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [lotteries, setLotteries] = useState([]);
  const [results, setResults] = useState({});
  const [predictions, setPredictions] = useState({});
  const [loading, setLoading] = useState(true);
  const [generatingPredictions, setGeneratingPredictions] = useState(false);
  const [activeTab, setActiveTab] = useState('results');
  const [showPayment, setShowPayment] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (searchParams.get('prediction_payment') === 'processing') {
      toast.info('Estamos verificando tu pago. El crédito aparecerá cuando se confirme.');
      setSearchParams(previous => {
        const next = new URLSearchParams(previous);
        next.delete('prediction_payment');
        return next;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const fetchData = async () => {
    try {
      const lotteriesRes = await axios.get(`${API}/lotteries`);
      setLotteries(lotteriesRes.data);
      
      // Fetch latest result for each lottery
      const resultsMap = {};
      for (const lottery of lotteriesRes.data) {
        try {
          const res = await axios.get(`${API}/results?lottery_id=${lottery.id}&limit=1`);
          if (res.data.length > 0) {
            resultsMap[lottery.id] = res.data[0];
          }
        } catch (e) {
          console.log(`No results for ${lottery.name}`);
        }
      }
      setResults(resultsMap);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const generateAllPredictions = async () => {
    if (!token) {
      toast.error('Debes iniciar sesión para ver pronósticos');
      return;
    }

    setGeneratingPredictions(true);
    const predictionsMap = {};
    
    for (const lottery of lotteries) {
      try {
        const res = await axios.post(
          `${API}/recommendations`,
          { lottery_id: lottery.id, strategy: 'balanced' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        predictionsMap[lottery.id] = res.data;
      } catch (e) {
        if (e.response?.status === 402) {
          setGeneratingPredictions(false);
          setShowPayment(true);
          return;
        }
        try {
          const res = await axios.post(
            `${API}/recommendations`,
            { lottery_id: lottery.id, strategy: 'conservative' },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          predictionsMap[lottery.id] = res.data;
        } catch (e2) {
          if (e2.response?.status === 402) {
            setGeneratingPredictions(false);
            setShowPayment(true);
            return;
          }
          console.log(`Cannot generate for ${lottery.name}: ${e2.response?.data?.detail}`);
        }
      }
    }
    
    setPredictions(predictionsMap);
    setGeneratingPredictions(false);
    toast.success('Pronósticos generados');
  };

  const getCompanyLotteries = () => {
    const companies = {};
    lotteries.forEach(lottery => {
      if (!companies[lottery.company]) {
        companies[lottery.company] = [];
      }
      companies[lottery.company].push(lottery);
    });
    return companies;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
      return date.toLocaleDateString('es-DO', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
    return dateStr;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  const companyLotteries = getCompanyLotteries();

  return (
    <div className="min-h-screen bg-slate-950 pb-20">
      <Navbar />
      
      <main className="pt-20 px-4 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2" data-testid="dashboard-title">
              Loterías Dominicanas
            </h1>
            <p className="text-slate-400">Resultados y Pronósticos del Día</p>
          </div>
          
          <div className="flex items-center gap-3">
            <Button 
              onClick={generateAllPredictions}
              disabled={generatingPredictions}
              className="btn-primary"
              data-testid="generate-all-predictions"
            >
              {generatingPredictions ? (
                <span className="flex items-center gap-2">
                  <RefreshCw size={18} className="animate-spin" />
                  Generando...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Sparkles size={18} />
                  Generar Pronósticos
                </span>
              )}
            </Button>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mb-6">
          <DisclaimerBanner variant="dismissible" />
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="bg-slate-900 border border-slate-800">
            <TabsTrigger 
              value="results" 
              className="data-[state=active]:bg-green-600"
              data-testid="tab-results"
            >
              <Calendar size={16} className="mr-2" />
              Resultados
            </TabsTrigger>
            <TabsTrigger 
              value="predictions" 
              className="data-[state=active]:bg-green-600"
              data-testid="tab-predictions"
            >
              <Sparkles size={16} className="mr-2" />
              Pronósticos
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Lottery Grid - 3 columns like reference image */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {lotteries.map((lottery) => (
            <LotteryCard 
              key={lottery.id}
              lottery={lottery}
              result={results[lottery.id]}
              prediction={predictions[lottery.id]}
              showPrediction={activeTab === 'predictions'}
            />
          ))}
        </div>

        {/* Quick Links */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Sócrates - Featured */}
          <Link to="/super-ia" className="block md:col-span-2 lg:col-span-1">
            <Card className="glass-card border-purple-500/30 hover:border-purple-400/50 transition-all overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 via-pink-600/10 to-purple-600/10 group-hover:opacity-100 opacity-0 transition-opacity" />
              <CardContent className="flex items-center gap-4 py-6 relative">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center animate-pulse">
                  <Brain size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="text-white font-bold">Sócrates</h3>
                  <p className="text-purple-300 text-sm">Predicciones con Sócrates</p>
                </div>
                <ArrowRight size={20} className="text-purple-400 ml-auto group-hover:translate-x-1 transition-transform" />
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/analysis" className="block">
            <Card className="glass-card hover:border-blue-500/50 transition-all">
              <CardContent className="flex items-center gap-4 py-6">
                <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <BarChart3 size={24} className="text-blue-400" />
                </div>
                <div>
                  <h3 className="text-white font-bold">Análisis Estadístico</h3>
                  <p className="text-slate-400 text-sm">Ver frecuencias y tendencias</p>
                </div>
                <ArrowRight size={20} className="text-slate-500 ml-auto" />
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/recommendations" className="block">
            <Card className="glass-card hover:border-green-500/50 transition-all">
              <CardContent className="flex items-center gap-4 py-6">
                <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Sparkles size={24} className="text-green-400" />
                </div>
                <div>
                  <h3 className="text-white font-bold">Recomendaciones</h3>
                  <p className="text-slate-400 text-sm">Generar números inteligentes</p>
                </div>
                <ArrowRight size={20} className="text-slate-500 ml-auto" />
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/history" className="block">
            <Card className="glass-card hover:border-amber-500/50 transition-all">
              <CardContent className="flex items-center gap-4 py-6">
                <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <Clock size={24} className="text-amber-400" />
                </div>
                <div>
                  <h3 className="text-white font-bold">Historial</h3>
                  <p className="text-slate-400 text-sm">Ver recomendaciones pasadas</p>
                </div>
                <ArrowRight size={20} className="text-slate-500 ml-auto" />
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/bot-diario" className="block" data-testid="bot-diario-link">
            <Card className="glass-card hover:border-cyan-500/50 transition-all bg-gradient-to-r from-emerald-900/20 to-cyan-900/20 border-emerald-500/30">
              <CardContent className="flex items-center gap-4 py-6">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500/30 to-cyan-500/30 flex items-center justify-center">
                  <Brain size={24} className="text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-white font-bold">Bot Diario</h3>
                  <p className="text-emerald-300/70 text-sm">3 numeros optimizados por loteria</p>
                </div>
                <ArrowRight size={20} className="text-emerald-500 ml-auto" />
              </CardContent>
            </Card>
          </Link>
        </div>
      </main>

      {/* Fixed Disclaimer */}
      <DisclaimerBanner variant="fixed" />
      <PredictionPaymentModal
        isOpen={showPayment}
        onClose={() => setShowPayment(false)}
        onPaymentComplete={() => { setShowPayment(false); generateAllPredictions(); }}
      />
    </div>
  );
};

// Lottery logos mapping - per lottery name (original logos from user)
const lotteryLogos = {
  'Gana Más': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/l2etb5gf_gana-mas.png',
  'Lotería Nacional': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/tmvw86yr_7827e5ee87f05e864aa79f64210269c5.png',
  'Quiniela Leidsa': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/kh7pktnz_quiniela-leidsa.png',
  'Quiniela Real': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/fx8q4po4_loteria-real.png',
  'Quiniela Loteka': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/f0ftl49v_quiniela-loteka.png',
  'Florida Día': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/3bjyf4yg_florida-dia.png',
  'Florida Noche': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/3bjyf4yg_florida-dia.png',
  'New York Tarde': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/la8m41e6_new-york-noche.png',
  'New York Noche': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/la8m41e6_new-york-noche.png',
  'La Primera Día': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/own2l5v1_la-primera-dia.png',
  'Primera Noche': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/own2l5v1_la-primera-dia.png',
  'La Suerte 12:30': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/vz6qo477_la-suerte-dia.png',
  'La Suerte 18:00': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/vz6qo477_la-suerte-dia.png',
  'Quiniela LoteDom': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/g0saehi3_d46144f7e1f664fee5ba811f984c246e.png',
  'Anguila Mañana': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/vg0ch0n1_anguila-manana-10am.png',
  'Anguila Medio Día': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/vg0ch0n1_anguila-manana-10am.png',
  'Anguila Tarde': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/vg0ch0n1_anguila-manana-10am.png',
  'Anguila Noche': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/vg0ch0n1_anguila-manana-10am.png',
  'King Lottery 12:30': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/y8q9q99k_quiniela-king-lottery-dia.png',
  'King Lottery 7:30': 'https://customer-assets.emergentagent.com/job_8cf4ad52-e08b-494c-b543-9f81ae43cb73/artifacts/y8q9q99k_quiniela-king-lottery-dia.png'
};

// Lotteries with original logos (black background) vs generated (white bg)
const originalLogos = new Set([
  'Gana Más', 'Lotería Nacional', 'Quiniela Leidsa', 'Quiniela Real', 'Quiniela Loteka',
  'Florida Día', 'Florida Noche', 'New York Tarde', 'New York Noche',
  'La Primera Día', 'Primera Noche', 'La Suerte 12:30', 'La Suerte 18:00', 'Quiniela LoteDom',
  'Anguila Mañana', 'Anguila Medio Día', 'Anguila Tarde', 'Anguila Noche',
  'King Lottery 12:30', 'King Lottery 7:30'
]);

// Lottery Card Component - Exact copy of reference design
const LotteryCard = ({ lottery, result, prediction, showPrediction }) => {
  const numbers = showPrediction 
    ? prediction?.numbers 
    : result?.numbers;
  
  // Determine if result is from today using DOMINICAN REPUBLIC time (UTC-4)
  // Esto evita confusion de timezone: el app es para RD asi que siempre comparamos vs fecha RD
  const drNow = new Date(Date.now() - 4 * 60 * 60 * 1000);
  const today = `${drNow.getUTCFullYear()}-${String(drNow.getUTCMonth() + 1).padStart(2, '0')}-${String(drNow.getUTCDate()).padStart(2, '0')}`;
  const drawDate = result?.draw_date || '';
  // Normalize drawDate: take only YYYY-MM-DD part (handles full ISO strings too)
  const drawDateOnly = drawDate.slice(0, 10);
  const isToday = drawDateOnly === today;

  // Display date from result
  const displayDate = drawDate 
    ? drawDate.split('-').reverse().join('-')
    : '--/--/----';

  // Company colors exactly matching the image
  const companyColors = {
    'Nacional': '#22c55e',      // Green
    'Leidsa': '#eab308',        // Yellow
    'Lotería Real': '#dc2626',  // Red
    'Loteka': '#22c55e',        // Green
    'Americanas': '#1e3a8a',    // Dark Blue
    'La Primera': '#dc2626',    // Red
    'La Suerte': '#3b82f6',     // Blue
    'LoteDom': '#1e3a8a',       // Dark Blue
    'Anguila': '#f97316',       // Orange
    'King Lottery': '#6b7280'   // Gray
  };

  const headerColor = companyColors[lottery.company] || lottery.color || '#22c55e';
  const logoUrl = lotteryLogos[lottery.name];
  const isOriginalLogo = originalLogos.has(lottery.name);

  // Ball colors: green for today, gray for older results
  const ballColor = showPrediction
    ? '#22c55e'
    : isToday ? '#22c55e' : '#6b7280';
  const emptyBallColor = '#6b7280';

  return (
    <Link to={`/lottery/${lottery.id}`}>
      <div 
        className="bg-gray-100 rounded-2xl overflow-hidden shadow-md hover:shadow-lg transition-all hover:scale-[1.02] cursor-pointer"
        data-testid={`lottery-card-${lottery.code}`}
      >
        {/* Company Header - Pill shape */}
        <div 
          className="py-2 px-6 text-center"
          style={{ backgroundColor: headerColor }}
        >
          <span className="text-white font-bold text-lg">{lottery.company}</span>
        </div>
        
        {/* Content */}
        <div className="p-4 bg-gray-100">
          {/* Date, Time and Lottery Name with Logo */}
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="flex items-center gap-3 text-gray-600 text-sm mb-1">
                <span>{displayDate}</span>
                <span className="flex items-center gap-1">
                  <Clock size={14} />
                  {lottery.draw_time || '--:--'}
                </span>
              </div>
              <p className="text-gray-800 font-bold text-lg">{lottery.name}</p>
            </div>
            {/* Logo - Real company logo */}
            <div 
              className={`w-16 h-16 rounded-xl shrink-0 overflow-hidden shadow-sm ${isOriginalLogo ? 'bg-gray-900' : 'bg-white border border-gray-200'}`}
              data-testid={`lottery-logo-${lottery.code}`}
            >
              {logoUrl ? (
                <img 
                  src={logoUrl} 
                  alt={`${lottery.name} logo`}
                  className="w-full h-full object-cover"
                  style={{ imageRendering: 'auto' }}
                  loading="lazy"
                />
              ) : (
                <div 
                  className="w-full h-full flex items-center justify-center text-white text-xs font-bold"
                  style={{ backgroundColor: headerColor }}
                >
                  {lottery.company.charAt(0)}
                </div>
              )}
            </div>
          </div>
          
          {/* Numbers - Colored balls: green=today, gray=older */}
          <div className="flex justify-start gap-3">
            {numbers ? (
              numbers.slice(0, 3).map((num, idx) => (
                <div 
                  key={`ball-${num}-${idx}`}
                  className="w-16 h-16 aspect-square shrink-0 rounded-full flex items-center justify-center text-white font-black text-2xl shadow-md"
                  style={{ backgroundColor: ballColor }}
                >
                  {String(num).padStart(2, '0')}
                </div>
              ))
            ) : (
              [0, 1, 2].map((idx) => (
                <div 
                  key={`empty-${idx}`}
                  className="w-16 h-16 aspect-square shrink-0 rounded-full flex items-center justify-center text-white font-black text-2xl"
                  style={{ backgroundColor: emptyBallColor }}
                >
                  --
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default Dashboard;
